/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AtButton,
  AtButtonProps,
  AtCheckbox,
  AtCheckboxProps,
  AtDatePicker,
  AtDatePickerProps,
  AtDropdown,
  AtDropdownProps,
  AtTabs,
  AtTextArea,
  AtTextAreaProps,
  AtTextField,
  AtTextFieldProps,
  DropdownOption,
  Holiday,
  Tab,
} from '../../../atoms'
import { Dispatch, FormEvent, FormEventHandler, Fragment, ReactNode, useMemo, useCallback, useReducer } from 'react'
import { MlCtaBar, MlCtaBarProps } from '../../../molecules'
import { DateRange } from 'react-day-picker'
import { isValidEmail } from '../../../atoms/at-text-field/shared/utils'
import { ConditionalField } from './conditional-field'
import { TimeSelect, TimeSelectProps, Timeslot } from './time-select'
import { labelClasses } from '../../../atoms/at-text-field/shared/classes'
import { AtButtonBehaviors, AtButtonDimensions, AtButtonTypes } from '../../../types'
import { cn } from '../../../utils'

export type FieldType = string | boolean | Date | DateRange | DropdownOption | Timeslot | number | undefined

export type FormFieldState = {
  [field: string]: {
    value?: FieldType
    disabled?: boolean
    pristine?: boolean
    error?: boolean
    loading?: boolean
    open?: boolean
  }
}

export type FormAction = { tag: 'update'; newState: FormFieldState } | { tag: 'reset' }

export const useFormState = (
  initialState: FormFieldState,
  initializer?: () => FormFieldState
): [FormFieldState, Dispatch<FormFieldState>, Dispatch<void>] => {
  const [state, reducer] = useReducer(
    (prevState: FormFieldState, action: FormAction) => {
      switch (action.tag) {
        case 'update': {
          return { ...prevState, ...action.newState }
        }

        case 'reset': {
          return initialState
        }
      }
    },
    initialState,
    initializer ? initializer : (state) => state
  )

  const update = useCallback((newState: FormFieldState) => reducer({ tag: 'update', newState }), [])
  const reset = useCallback(() => reducer({ tag: 'reset' }), [])

  return [state, update, reset]
}

export type FormField = { container?: string } & (
  | ({ type: 'text' } & Omit<AtTextFieldProps, 'value' | 'onChange' | 'clear' | 'hasError' | 'disabled'>)
  | ({ type: 'text-area'; collapsible?: boolean; defaultExpanded?: boolean } & Omit<
      AtTextAreaProps,
      'value' | 'onChange' | 'clear' | 'hasError' | 'disabled'
    >)
  | ({
      type: 'checkbox'
      required?: boolean
    } & Omit<AtCheckboxProps, 'onChange' | 'hasError' | 'isChecked' | 'disabled' | 'className'>)
  | { type: 'divider'; className?: string }
  | { type: 'label'; name: string; label: string; className?: string; required?: boolean }
  | ({
      type: 'calendar'
      required?: boolean
      subCheckboxName?: string
      subCheckboxLabel?: string
    } & Omit<AtDatePickerProps, 'value' | 'onChange' | 'hasError'>)
  | ({ type: 'dropdown'; required?: boolean } & Omit<AtDropdownProps, 'value' | 'onChange' | 'hasError'>)
  | ({
      type: 'conditional-checkbox'
      fields: FormField[]
    } & Omit<AtCheckboxProps, 'onChange' | 'hasError' | 'isChecked' | 'disabled' | 'className'>)
  | { type: 'tab'; name?: string; label?: string; tabs: (Omit<Tab, 'content'> & { content: FormField[] })[] }
  | ({ type: 'time-select'; name: string } & Omit<TimeSelectProps, 'value' | 'onChange' | 'error' | 'loading'>)
  | { type: 'grid'; label?: string; fields: FormField[] }
  /* TODO: Placeholders for possible uses based on UI definition
  | { type: 'rich-text' }
  | { type: 'radio' }
  */
  | ({ type: 'submit'; label: string; className?: string } & Pick<
      AtButtonProps,
      'IconRightRef' | 'IconLeftRef' | 'isDisabled'
    >)
  | { type: 'custom'; children: ReactNode }
)

export interface FormProps extends MlCtaBarProps {
  fields: FormField[]
  state: FormFieldState
  bottomContent?: ReactNode
  onSubmit?: (valid: boolean, errors: string[], e: FormEvent<HTMLFormElement>) => void
  onChange: (change: FormFieldState) => void
  fieldClassName?: string
  skipCtaBar?: boolean // TODO: make cta bar another field type, this would remove `bottomContent` too
  overrideClass?: boolean
  formClassName?: string
  gridClassName?: string
  ctaClassName?: string
  id?: string
  name?: string
  isCheckCleared?: boolean
  setIsCheckCleared?: (arg0: boolean) => void
  holidays?: Holiday
}

export const Form = ({
  bottomContent,
  formClassName = 'pt-6 lg:pt-8 lg:pb-8',
  fieldClassName = '',
  gridClassName = 'lg:grid-cols-2',
  ctaClassName = '',
  fields,
  state,
  onChange,
  onSecondaryClick,
  onSubmit,
  setIsCheckCleared,
  isCheckCleared,
  primaryLabel,
  primaryIconRef,
  secondaryLabel,
  skipCtaBar,
  overrideClass,
  id,
  primaryDisabled,
  secondaryDisabled,
  holidays,
}: FormProps) => {
  const hasErrors = useMemo(() => Object.values(state).some((field) => field.error), [state])
  const fieldMapFn = (field: FormField, i?: number) => {
    if (field.container)
      return (
        <div className={field.container} key={i}>
          {fieldMapFn({ ...field, container: undefined })}
        </div>
      )

    if (field.type === 'divider')
      return <div key={i} className={`w-full h-[1px] bg-neutrals-300 ${field.className ?? ''}`} />

    if (field.type === 'custom') return <Fragment key={i}>{field.children}</Fragment>

    if (field.type === 'tab')
      return (
        <AtTabs
          {...(field.name
            ? {
                selectedTab: state[field.name].value as number,
                onChange: (i) =>
                  onChange?.({
                    [field.name!]: {
                      value: i,
                    },
                  }),
              }
            : {})}
          key={i}
          tabs={field.tabs.map((tab) => ({ ...tab, content: <div>{tab.content.map(fieldMapFn)}</div> }))}
        />
      )

    if (field.type === 'grid')
      return (
        <div key={i}>
          {field.label && <p className={`${labelClasses} mb-4`}>{field.label}</p>}
          <div className={cn('grid gap-4 lg:gap-x-7.5 lg:gap-y-6', gridClassName)}>{field.fields.map(fieldMapFn)}</div>
        </div>
      )

    if (field.type === 'label')
      return (
        <p key={i} className={`${field.className} text-[18px] font-bold mb-[17px]`}>
          {field.label}
          {field.required && <span className="text-feedback-negative-300 ml-1">*</span>}
        </p>
      )

    if (field.type === 'submit')
      return (
        <AtButton
          key={i}
          type={AtButtonTypes.PRIMARY}
          size={AtButtonDimensions.LARGE}
          typeButton={AtButtonBehaviors.SUBMIT}
          IconRightRef={field.IconRightRef}
          IconLeftRef={field.IconLeftRef}
          isDisabled={field.isDisabled}
          title={field.label}
          className={cn(field.className, ctaClassName)}
          parentName="Form"
        />
      )

    const { value, error, disabled, loading } = state[field.name] ?? {}
    const changeFn = (value: FieldType) =>
      onChange?.({
        [field.name]: {
          ...state[field.name],
          value,
          error: false,
          pristine: false,
        },
      })

    switch (field.type) {
      case 'conditional-checkbox':
        return (
          <ConditionalField
            key={i}
            {...field}
            isChecked={value as boolean}
            hasError={error}
            disabled={disabled}
            onChange={changeFn}
          >
            {field.fields.map(fieldMapFn)}
          </ConditionalField>
        )

      case 'checkbox':
        return (
          <div key={i} id={`${field.name}-field`}>
            <AtCheckbox
              {...field}
              isChecked={value as boolean}
              isCheckCleared={isCheckCleared}
              setIsCheckCleared={setIsCheckCleared}
              hasError={error}
              disabled={disabled}
              onChange={changeFn}
            />
          </div>
        )
      case 'text-area': {
        const isOpen = state[field.name]?.open ?? (!!field.required || !!field.defaultExpanded)
        if (field.collapsible)
          return (
            <div key={i} className="flex flex-col gap-3">
              <button
                type="button"
                className="flex items-center gap-2 text-16 font-bold text-neutrals-900 w-fit"
                onClick={() => onChange?.({ [field.name]: { ...state[field.name], open: !isOpen } })}
              >
                <span className="text-20 leading-none">{isOpen ? '—' : '+'}</span>
                {field.label}
                {field.required && <span className="text-feedback-negative-300 ml-1">*</span>}
              </button>
              {isOpen && (
                <AtTextArea
                  {...field}
                  label={undefined}
                  required={undefined}
                  value={value as string}
                  hasError={error}
                  disabled={disabled}
                  onChange={changeFn}
                />
              )}
            </div>
          )
        return (
          <AtTextArea
            key={i}
            {...field}
            value={value as string}
            hasError={error}
            disabled={disabled}
            onChange={changeFn}
          />
        )
      }
      case 'text':
        return (
          <AtTextField
            key={i}
            {...field}
            value={value as string}
            hasError={error}
            disabled={disabled}
            type={field.validator === 'email' ? 'email' : 'text'}
            onChange={changeFn}
            clear={() => changeFn('')}
          />
        )
      case 'calendar': {
        const { subCheckboxName, subCheckboxLabel } = field
        return (
          <div key={i} className="flex flex-col gap-3">
            <AtDatePicker
              {...field}
              value={value as Date | DateRange}
              hasError={error}
              open={state[field.name]?.open}
              onClick={(open) => onChange?.({ [field.name]: { ...state[field.name], open } })}
              onChange={(value) => {
                if (value && 'to' in value && value.to && state[field.name]?.pristine) {
                  const { to } = value as DateRange
                  changeFn({ from: to } as DateRange)
                } else changeFn(value)
              }}
              holidays={holidays}
            />
            {subCheckboxName && subCheckboxLabel && (
              <AtCheckbox
                name={subCheckboxName}
                label={subCheckboxLabel}
                isChecked={(state[subCheckboxName]?.value as boolean) ?? false}
                hasError={state[subCheckboxName]?.error}
                onChange={(checked) =>
                  onChange?.({ [subCheckboxName]: { ...state[subCheckboxName], value: checked, error: false } })
                }
              />
            )}
          </div>
        )
      }
      case 'dropdown':
        return <AtDropdown key={i} {...field} value={value as DropdownOption} hasError={error} onChange={changeFn} />
      case 'time-select':
        return (
          <TimeSelect
            key={i}
            {...field}
            loading={loading}
            error={error}
            value={value as Timeslot}
            onChange={changeFn}
          />
        )
      default:
        throw new Error('Invalid field type specified')
    }
  }

  const validateFields = (formFields: FormField[]): { valid: boolean; errors: string[] } => {
    let validForm = true
    const withErrors: string[] = []

    formFields.forEach((field) => {
      if (field.type === 'tab') {
        const tabs = field.tabs[state[field.name!].value as number]
          ? [field.tabs[state[field.name!].value as number]]
          : field.tabs
        tabs.forEach((tab) => {
          const result = validateFields(tab.content)
          validForm &&= result.valid
          withErrors.push(...result.errors)
        })
        return
      }

      if (field.type === 'grid') {
        const result = validateFields(field.fields)
        validForm &&= result.valid
        withErrors.push(...result.errors)
        return
      }

      if (field.type === 'conditional-checkbox') {
        if (state[field.name]?.value) {
          const result = validateFields(field.fields)
          validForm &&= result.valid
          withErrors.push(...result.errors)
        }
        return
      }

      if (field.type === 'checkbox' && field.required) {
        const valid = !!state[field.name]?.value
        validForm &&= valid
        if (!valid) withErrors.push(field.name)
        onChange?.({ [field.name]: { ...state[field.name], error: !valid } })
      }

      if (field.type === 'time-select' && field.name) {
        const valid = !!(state[field.name]?.value as Timeslot)
        validForm &&= valid
        if (!valid) {
          withErrors.push(field.name)
        }
        onChange?.({ [field.name]: { ...state[field.name], error: !valid } })
      }

      if (field.type === 'text' && field.validator === 'email' && field.name) {
        const value = state[field.name]?.value as string | undefined
        if (value?.trim() && !isValidEmail(value)) {
          validForm = false
          withErrors.push(field.name)
          onChange?.({ [field.name]: { ...state[field.name], error: true } })
        }
      }

      if (field.type === 'dropdown' && field.name) {
        const valid = !field.required || (field.required && !!(state[field.name]?.value as DropdownOption)?.value)
        validForm &&= valid
        if (!valid) {
          withErrors.push(field.name)
        }
        onChange?.({ [field.name]: { ...state[field.name], error: !valid } })
      }

      if (field.type === 'calendar' && field.name) {
        const mode = field.mode ?? 'single'
        const valid =
          !field.required ||
          (field.required &&
            !!(
              (mode === 'single' && state[field.name]?.value) ||
              (mode === 'range' && (state[field.name]?.value as DateRange)?.from)
            ))

        validForm &&= valid
        if (!valid) {
          withErrors.push(field.name)
        }
        onChange?.({ [field.name]: { ...state[field.name], error: !valid } })
      }
    })

    return { valid: validForm, errors: withErrors }
  }

  const checkValidity: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    let validForm = true
    const withErrors: string[] = []

    const input = Array.from(e.currentTarget) as HTMLInputElement[]
    input.forEach((field) => {
      if (!field.name || !state[field.name]) {
        return
      }
      // Native `required` accepts whitespace-only values, so treat them as empty
      const isBlankRequired =
        field.required && ['text', 'textarea'].includes(field.type) && field.value.length > 0 && !field.value.trim()
      const valid = field.checkValidity() && !isBlankRequired
      validForm &&= valid

      if (!valid) {
        withErrors.push(field.name)
      }
      onChange?.({ [field.name]: { ...state[field.name], error: !valid } })
    })

    const customValidation = validateFields(fields)

    validForm &&= customValidation.valid
    withErrors.push(...customValidation.errors)

    const finalErrors = Array.from(new Set(withErrors))

    onSubmit?.(validForm, finalErrors, e)
  }

  return (
    <form noValidate onSubmit={checkValidity} className={formClassName} id={id} suppressHydrationWarning={true}>
      {fields && (
        <div className={`${overrideClass ? '' : 'flex flex-col'} ${fieldClassName}`}>{fields.map(fieldMapFn)}</div>
      )}
      {!skipCtaBar && (
        <>
          <div className="lg:mt-6 p-4 lg:py-0 lg:px-8 fixed bottom-0 lg:static w-full bg-neutrals-white">
            <MlCtaBar
              primaryLabel={primaryLabel}
              primaryIconRef={primaryIconRef}
              primaryDisabled={primaryDisabled || hasErrors}
              secondaryLabel={secondaryLabel}
              secondaryDisabled={secondaryDisabled}
              onSecondaryClick={onSecondaryClick}
              parentName="OrBookingPanelForm"
            />
          </div>
          {bottomContent}
          <div aria-hidden className={`lg:hidden w-full ${secondaryLabel ? 'h-[11.5rem]' : 'h-32'}`} />
        </>
      )}
    </form>
  )
}
